import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { closeModalOnEscape } from '../../../modal-dialog/modal-dialog.helper';
import { CreateTenantLevelInventoryResourceDialogPayload } from './inventory-resource-action-dialog-payload.model';
import { InventoryResourceType, SecretWrapperWithUsage, TenantResourceFlag } from '../../../inventory/inventory.model';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, ValidationErrors, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { InventoryService } from '../../../inventory/inventory.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHelper } from '../../../common/helpers/error.helper';
import { ModalNotificationService } from '../../../notification/modal-notification.service';
import { ToastNotificationService } from '../../../notification/toast-notification.service';
import { InventoryAttachFilePayload, InventoryAttachmentTenantResourceMode } from '../../../inventory/inventory-editor/insert-inventory-attachments-dialog/inventory-attachment-payload.model';
import { InsertInventoryAttachmentsDialogService } from '../../../inventory/inventory-editor/insert-inventory-attachments-dialog/insert-inventory-attachments-dialog.service';
import { map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'adm4-create-tenant-level-inventory-resource-dialog',
    template: `
    <form name='secretForm' class='secret-form' [formGroup]="form" (ngSubmit)='onFormSubmit()'>
      <adm4-modal-dialog-title class='modal-dialog-title'
        [header]='header'
        [showClose]='true'
        [isFullHeightContent]='true'
        (closeClicked)="closeDialog()">
        <div class='full-height-flex'>
          <div mat-dialog-content class='secret-content-wrapper content-container'>
            <adm4-admonition class="mb-4" severity="info" alignment="start">
              <p adm4-admonition-msg class="m-0 color-info">
                To use the {{payload.inventoryResourceType | lowercase}} in your inventory, open the inventory editor and add the reference there.
              </p>
            </adm4-admonition>
            @if (inventoryResourceType !== InventoryResourceType.PLAIN_TEXT_SECRET) {
              <!-- Unified in-dialog file picker for both FILE and SECRET_FILE types (Row 6) -->
              <div class='adm4-form-group'>
                <button type="button" class="admn4-button-ellipse-blue" (click)="fileInput.click()">
                  Select File
                </button>
                @if (fileAttachment) {
                  <span class="file-name-display">{{ fileAttachment.name }}</span>
                }
                <input type="file" #fileInput style="display:none" (change)="onFileSelected($event)">
              </div>
              <!-- Row 8: warn when a regular file upload will be stored as a K8s secret file -->
              @if (inventoryResourceType === InventoryResourceType.FILE && form.get('type')?.value === 'kubernetes') {
                <adm4-admonition class="mb-4" severity="warning" alignment="start">
                  <p adm4-admonition-msg class="m-0">
                    This file will be uploaded as a <strong>Kubernetes secret file</strong> and will appear in the Secret Files section, not the Files section.
                  </p>
                </adm4-admonition>
              }
            } @else {
              <label class='input-label'>Secret</label>
              <div class='adm4-form-group admn4-input-group-with-icon'>
                <input id="secretInput" class='admn4-text-input form-control' cdkFocusInitial placeholder='Please enter value' [type]="hide ? 'password' : 'text'" required [formControlName]='SECRET_TEXT_FORM_CONTROL_NAME' autocomplete="off">
                <mat-icon matSuffix (click)="hide = !hide" class='input-icon visibility-icon admn4-button-ellipse-blue'>{{hide ? 'visibility' : 'visibility_off'}}</mat-icon>
              </div>
            }
            <div class='adm4-form-group'>
              <label class="input-label">Type</label>
              <mat-form-field class="full-width">
                <mat-select formControlName="type" placeholder="Select type">
                  @if (hasTenantModifyPermission$ | async) {
                    <mat-option value="internal">Internal</mat-option>
                  }
                  @if ((inventoriesWithModifyPermission$ | async)?.length; as hasInventories) {
                    <mat-option value="kubernetes">Kubernetes</mat-option>
                  } @else {
                    <mat-option value="kubernetes" disabled>Kubernetes</mat-option>
                  }
                </mat-select>
                @if (!(inventoriesWithModifyPermission$ | async)?.length) {
                  <mat-hint>Requires modify permission on at least one inventory</mat-hint>
                }
              </mat-form-field>
            </div>
            <div class="adm4-form-group">
              <label class="input-label">Scope</label>
              <mat-form-field class="full-width">
                <!-- Row 15: scope is auto-set and disabled for Internal type -->
                <mat-select formControlName="scope" placeholder="Select scope"
                  [disableRipple]="true"
                  [disableOptionCentering]="true">
                  @for (scopeOption of filteredScopeOptions$ | async; track scopeOption) {
                    <mat-option [value]="scopeOption.value">
                      {{ scopeOption.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (form.get('type')?.value === 'internal' && inventoryResourceType === InventoryResourceType.PLAIN_TEXT_SECRET) {
                <small class="text-muted">Internal secrets are always created with Global scope.</small>
              }
            </div>
            @if (form.get('type')?.value === 'kubernetes') {
              <div class="adm4-form-group">
                <label class="input-label">Secret Name</label>
                <div class="admn4-form-group">
                  <input class="admn4-text-input form-control"
                    [class.is-invalid]="form.get(SECRET_NAME_FORM_CONTROL_NAME)?.invalid && form.get(SECRET_NAME_FORM_CONTROL_NAME)?.dirty"
                    placeholder="Enter secret name"
                    [formControlName]="SECRET_NAME_FORM_CONTROL_NAME"
                    autocomplete="off">
                  @if (form.get(SECRET_NAME_FORM_CONTROL_NAME)?.errors?.['systemSecret']) {
                    <div class="invalid-feedback">
                      This secret is managed by the system and cannot be modified.
                    </div>
                  }
                  @if (form.get(SECRET_NAME_FORM_CONTROL_NAME)?.errors?.['pattern']) {
                    <div class="invalid-feedback">
                      Name may only contain letters, digits, hyphens and dots, and must start with a letter or digit.
                    </div>
                  }
                  @if (form.get(SECRET_NAME_FORM_CONTROL_NAME)?.errors?.['maxlength']) {
                    <div class="invalid-feedback">
                      Name must not exceed 253 characters.
                    </div>
                  }
                </div>
              </div>
            }
            @if (form.get('type')?.value === 'kubernetes' && inventoryResourceType === InventoryResourceType.PLAIN_TEXT_SECRET) {
              <div class="adm4-form-group">
                <label class="input-label">Secret Key</label>
                <div class="admn4-form-group">
                  <input class="admn4-text-input form-control"
                    [class.is-invalid]="form.get(SECRET_KEY_FORM_CONTROL_NAME)?.invalid && form.get(SECRET_KEY_FORM_CONTROL_NAME)?.dirty"
                    placeholder="Enter secret key"
                    [formControlName]="SECRET_KEY_FORM_CONTROL_NAME"
                    autocomplete="off">
                  @if (form.get(SECRET_KEY_FORM_CONTROL_NAME)?.errors?.['pattern']) {
                    <div class="invalid-feedback">
                      Key may only contain letters, digits, hyphens, underscores and dots, and must start with a letter or digit.
                    </div>
                  }
                  @if (form.get(SECRET_KEY_FORM_CONTROL_NAME)?.errors?.['maxlength']) {
                    <div class="invalid-feedback">
                      Key must not exceed 253 characters.
                    </div>
                  }
                </div>
              </div>
            }
            <div class='adm4-form-group'>
              <label class='input-label'>Secret Description (optional)</label>
              <textarea class='form-control admn4-textarea-input'
                placeholder='Add your description (max 150 characters)'
                maxlength='150'
                rows="2"
                cdkFocusInitial
              [formControlName]='DESCRIPTION_FORM_CONTROL_NAME'></textarea>
            </div>
          </div>
          <div mat-dialog-actions>
            <adm4-button-bar [isSubmitDisabled]="form.invalid || isSubmitDisabledDueToMissingFile()"
              [submitButtonText]='resolveSubmitBtnText()'
              wrapperClass="nowrap"
            (cancelClicked)='closeDialog()'></adm4-button-bar>
          </div>
        </div>
      </adm4-modal-dialog-title>
    </form>
    `,
    styleUrls: ['../../../common/styles/component-specific/modal-window.scss', './inventory-resource-action-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreateInventoryAttachTenantResourceDialogComponent implements OnInit, OnDestroy {
  payload = inject<CreateTenantLevelInventoryResourceDialogPayload>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<CreateInventoryAttachTenantResourceDialogComponent>>(MatDialogRef);
  private fb = inject(UntypedFormBuilder);
  private inventoryService = inject(InventoryService);
  private toastNotificationService = inject(ToastNotificationService);
  private modalNotificationService = inject(ModalNotificationService);
  private insertInventoryAttachmentsDialogService = inject(InsertInventoryAttachmentsDialogService);


  header: string;
  inventoryResourceType: string;
  fileAttachment: File | undefined;
  hasTenantModifyPermission$: Observable<boolean>;
  inventoriesWithModifyPermission$: Observable<string[]>;

  public form: UntypedFormGroup;

  public SECRET_TYPE_FORM_CONTROL_NAME = 'type';
  public SCOPE_FORM_CONTROL_NAME = 'scope'; // inventory

  public SECRET_NAME_FORM_CONTROL_NAME = 'secretName';
  public SECRET_KEY_FORM_CONTROL_NAME = 'secretKey';
  public SECRET_TEXT_FORM_CONTROL_NAME = 'secretText';

  public DESCRIPTION_FORM_CONTROL_NAME = 'description';

  public hide = true;

  private destroyed$: Subject<boolean> = new Subject();
  protected filteredScopeOptions$ = new BehaviorSubject<Array<{ label: string; value: string }>>([]);

  constructor() {
    const payload = this.payload;

    this.inventoryResourceType = payload.inventoryResourceType;
    this.header = this.resolveHeaderByInventoryResourceType();
    this.fileAttachment = payload.file;
    this.hasTenantModifyPermission$ = payload.hasTenantModifyPermission;
    this.inventoriesWithModifyPermission$ = payload.inventoriesWithModifyPermission;
    closeModalOnEscape(this.dialogRef, this.destroyed$);
  }

  ngOnInit(): void {
    this.form = this.fb.group({});

    if (_.isEqual(this.inventoryResourceType, InventoryResourceType.PLAIN_TEXT_SECRET)) {
      this.form.addControl(this.SECRET_TEXT_FORM_CONTROL_NAME, new UntypedFormControl('', Validators.required));
    }

    // Always add type and scope
    this.form.addControl('type', new UntypedFormControl(null, Validators.required));
    this.form.addControl('scope', new UntypedFormControl(null, Validators.required));

    // Add controls for Kubernetes secrets, but disabled by default
    // Row 10: include format validation (alphanumeric, hyphens, dots) and max-length for name/key
    const k8sNamePattern = /^[a-zA-Z0-9][a-zA-Z0-9.-]*$/;
    const k8sKeyPattern  = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;
    this.form.addControl(this.SECRET_NAME_FORM_CONTROL_NAME, new UntypedFormControl('', [Validators.required, Validators.pattern(k8sNamePattern), Validators.maxLength(253), this.systemSecretNameValidator()]));
    // Secret Key is only needed for plain-text secrets — for file uploads the filename is the key
    if (_.isEqual(this.inventoryResourceType, InventoryResourceType.PLAIN_TEXT_SECRET)) {
      this.form.addControl(this.SECRET_KEY_FORM_CONTROL_NAME, new UntypedFormControl('', [Validators.required, Validators.pattern(k8sKeyPattern), Validators.maxLength(253)]));
    }

    // Description optional
    this.form.addControl(this.DESCRIPTION_FORM_CONTROL_NAME, new UntypedFormControl('', null));

    const typeControl = this.form.get('type')!;

    typeControl.valueChanges.subscribe(typeValue => {
      const secretNameControl = this.form.get(this.SECRET_NAME_FORM_CONTROL_NAME);
      if (!secretNameControl) return;

      const secretKeyControl = this.form.get(this.SECRET_KEY_FORM_CONTROL_NAME);

      if (typeValue?.toLowerCase() === 'kubernetes') {
        secretNameControl.enable();
        secretNameControl.setValidators([Validators.required, Validators.pattern(k8sNamePattern), Validators.maxLength(253), this.systemSecretNameValidator()]);
        if (secretKeyControl) {
          secretKeyControl.enable();
          secretKeyControl.setValidators([Validators.required, Validators.pattern(k8sKeyPattern), Validators.maxLength(253)]);
          secretKeyControl.updateValueAndValidity({ emitEvent: false });
        }
        this.form.get(this.SCOPE_FORM_CONTROL_NAME)?.enable();
      } else {
        secretNameControl.disable();
        secretNameControl.clearValidators();
        if (secretKeyControl) {
          secretKeyControl.disable();
          secretKeyControl.clearValidators();
          secretKeyControl.updateValueAndValidity({ emitEvent: false });
        }
        // Row 15: disable scope for Internal type — value is auto-set to GLOBAL
        this.form.get(this.SCOPE_FORM_CONTROL_NAME)?.disable();
      }

      secretNameControl.updateValueAndValidity({ emitEvent: false });
    });

    // Keep filteredScopeOptions$ reactive so it stays current when scopeOptions or
    // inventoriesWithModifyPermission emit new values while the dialog is open.
    // Scope reset is also driven here so it remains correct even when permissions
    // finish loading after the dialog opens (no take(1) snapshot).
    typeControl.valueChanges.pipe(
      startWith(typeControl.value as string | null),
      switchMap(typeValue =>
        combineLatest([this.payload.scopeOptions, this.payload.inventoriesWithModifyPermission, this.payload.kubernetesInventoryKeys]).pipe(
          map(([options, inventoriesWithModify, kubernetesInventoryKeys]) => ({
            filtered: this.computeFilteredScopeOptions(typeValue, options, inventoriesWithModify, kubernetesInventoryKeys),
            typeValue,
          }))
        )
      ),
      takeUntil(this.destroyed$),
    ).subscribe(({filtered, typeValue}) => {
      this.filteredScopeOptions$.next(filtered);

      // Reset the selected scope value when it is no longer among the filtered options.
      const currentValue = this.form.get('scope')?.value;
      if (!filtered.some(o => o.value === currentValue)) {
        if (typeValue?.toLowerCase() === 'internal') {
          const globalOption = filtered.find(o => o.value === this.payload.tenantKey);
          this.form.get('scope')?.setValue(globalOption?.value ?? null);
        } else {
          this.form.get('scope')?.setValue(null);
        }
      }
    });

    // Pre-select "internal" when user has MODIFY_TENANT; "kubernetes" otherwise.
    this.payload.hasTenantModifyPermission.pipe(take(1)).subscribe(hasTenantModify => {
      typeControl.setValue(hasTenantModify ? 'internal' : 'kubernetes');
    });

    // Re-run secretName validation whenever scope changes, because systemSecretNameValidator
    // checks the selected scope to filter existing secrets.
    this.form.get(this.SCOPE_FORM_CONTROL_NAME)?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.form.get(this.SECRET_NAME_FORM_CONTROL_NAME)?.updateValueAndValidity({ emitEvent: false });
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileAttachment = input.files[0];
      this.form.updateValueAndValidity();
    }
  }

  /** Row 6: disable submit when a file upload type is used but no file has been selected yet. */
  isSubmitDisabledDueToMissingFile(): boolean {
    return this.inventoryResourceType !== InventoryResourceType.PLAIN_TEXT_SECRET && !this.fileAttachment;
  }

  /** Returns a validator that rejects a secret name if it matches an existing system-managed (non-userCreated) Kubernetes secret in the selected scope. */
  private systemSecretNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name: string = control.value;
      if (!name) return null;
      const existingSecrets: (SecretWrapperWithUsage & TenantResourceFlag)[] = this.payload.existingKubernetesSecrets ?? [];
      const selectedScope = this.form?.get(this.SCOPE_FORM_CONTROL_NAME)?.value;
      const scopedSecrets = selectedScope
        ? existingSecrets.filter(s => s.scope === selectedScope)
        : existingSecrets;
      const isSystemSecret = scopedSecrets.some(s =>
        s.secretType === 'kubernetes' &&
        !s.userCreated &&
        s.secretId?.split(':')[0] === name
      );
      return isSystemSecret ? { systemSecret: true } : null;
    };
  }

  /**
   * Computes filtered scope options and resets the scope control value if needed.
   * In production the reactive pipeline in ngOnInit handles this continuously.
   * This method exists so that unit tests can exercise the filtering/reset logic
   * directly without wiring up the full reactive pipeline.
   */
  updateScopeField(typeValue: string | null) {
    combineLatest([
      this.payload.scopeOptions,
      this.payload.inventoriesWithModifyPermission,
      this.payload.kubernetesInventoryKeys,
    ]).pipe(take(1))
      .subscribe(([options, inventoriesWithModify, kubernetesInventoryKeys]: [Array<{ label: string; value: string }>, string[], string[]]) => {
        const filteredOptions = this.computeFilteredScopeOptions(typeValue, options, inventoriesWithModify, kubernetesInventoryKeys);

        this.filteredScopeOptions$.next(filteredOptions);

        // Reset the selected value if it's no longer in the filtered options
        const currentValue = this.form.get('scope')?.value;
        if (!filteredOptions.some(o => o.value === currentValue)) {
          const normalizedType = typeValue?.toLowerCase();
          if (normalizedType === 'internal') {
            const globalOption = filteredOptions.find(o => o.value === this.payload.tenantKey);
            this.form.get('scope')?.setValue(globalOption?.value ?? null);
          } else {
            this.form.get('scope')?.setValue(null);
          }
        }
      });
  }

  private computeFilteredScopeOptions(
    typeValue: string | null,
    options: Array<{ label: string; value: string }>,
    inventoriesWithModify: string[],
    kubernetesInventoryKeys: string[],
  ): Array<{ label: string; value: string }> {
    const normalizedType = typeValue?.toLowerCase();
    if (normalizedType === 'kubernetes') {
      // Only show K8s-schema inventories where the user also has MODIFY_INVENTORY permission
      return options.filter(o => o.value !== this.payload.tenantKey && inventoriesWithModify.includes(o.value) && kubernetesInventoryKeys.includes(o.value));
    } else if (normalizedType === 'internal') {
      // Only show GLOBAL (user must have MODIFY_TENANT, which is already guarded by the type option visibility)
      const globalOption = options.find(o => o.value === this.payload.tenantKey);
      return globalOption ? [globalOption] : [];
    }
    return options;
  }

  resolveHeaderByInventoryResourceType(): string {
    switch (this.inventoryResourceType) {
      case InventoryResourceType.PLAIN_TEXT_SECRET:
        return 'Create secret';
      case InventoryResourceType.SECRET_FILE:
        return 'Upload secret file';
      case InventoryResourceType.FILE:
      default:
        return 'Upload file';
    }
  }

  resolveSubmitBtnText(): string {
    if (_.isEqual(this.inventoryResourceType, InventoryResourceType.PLAIN_TEXT_SECRET)) {
      return 'Create';
    }
    return 'Upload';
  }

  onFormSubmit(): void {
    if (this.isSubmitDisabledDueToMissingFile()) {
      return;
    }
    const secretType = this.form.controls[this.SECRET_TYPE_FORM_CONTROL_NAME].value;
    if (this.fileAttachment) {
      if (secretType === 'kubernetes') {
        const inventoryKey = this.form.controls[this.SCOPE_FORM_CONTROL_NAME].value;
        const secretName = this.form.controls[this.SECRET_NAME_FORM_CONTROL_NAME].value;
        const secretDescription = this.form.controls[this.DESCRIPTION_FORM_CONTROL_NAME].value;
        this.inventoryService.createKubernetesSecretFile(inventoryKey, secretName, this.fileAttachment, secretDescription).subscribe(() => {
            this.toastNotificationService.showSuccessToast(`Kubernetes secret file has been successfully uploaded`, 'Successfully uploaded');
            this.payload.onSaveCallback();
            this.closeDialog();
        }, (error: HttpErrorResponse) => {
            this.modalNotificationService.openErrorDialog({title: 'Uploading Kubernetes secret file failed', description: ErrorHelper.getErrorDetail(error, 'Something went wrong with uploading Kubernetes secret file')});
        });
        return;
      }
      const inventoryAttachFilePayload = new InventoryAttachFilePayload();
      inventoryAttachFilePayload.file = this.fileAttachment;
      inventoryAttachFilePayload.description = this.form.controls[this.DESCRIPTION_FORM_CONTROL_NAME].value;
      this.insertInventoryAttachmentsDialogService.handleTenantLevelSecretInsert(this.dialogRef, this.payload.isSecret ? InventoryAttachmentTenantResourceMode.tenantLevelSecretResourceFile : InventoryAttachmentTenantResourceMode.tenantLevelResourceFile,
        this.payload.tenantKey).pipe(
        take(1)
      ).subscribe((inventoryAttachmentRefs) => {
        this.displayAttachmentResourceUploadToast(inventoryAttachmentRefs);
        this.payload.onSaveCallback();
      });
      this.dialogRef.close(inventoryAttachFilePayload);
      return;
    }
    const secretValue = this.form.controls[this.SECRET_TEXT_FORM_CONTROL_NAME].value;
    const secretDescription = this.form.controls[this.DESCRIPTION_FORM_CONTROL_NAME].value;
    if (secretType == 'kubernetes') {
      const inventoryKey = this.form.controls[this.SCOPE_FORM_CONTROL_NAME].value;
      const secretName = this.form.controls[this.SECRET_NAME_FORM_CONTROL_NAME].value;
      const secretKey = this.form.controls[this.SECRET_KEY_FORM_CONTROL_NAME].value;
      const secretId = `${secretName}:${secretKey}`;

      const existingSecrets: (SecretWrapperWithUsage & TenantResourceFlag)[] = this.payload.existingKubernetesSecrets ?? [];
      const duplicate = existingSecrets.find(s =>
        s.scope === inventoryKey &&
        s.secretType === 'kubernetes' &&
        s.userCreated &&
        s.secretId === secretId
      );

      const doCreate = (isOverwrite = false) =>
        this.inventoryService
          .createKubernetesSecret(
            inventoryKey,
            secretName,
            secretKey,
            secretValue,
            secretDescription
          )
          .subscribe({
            next: () => {
                const msg = isOverwrite
                    ? 'Kubernetes secret has been successfully overwritten'
                    : 'Kubernetes secret has been successfully created';
                const title = isOverwrite
                    ? 'Successfully overwritten'
                    : 'Successfully created';
                this.toastNotificationService.showSuccessToast(msg, title);
                this.payload.onSaveCallback();
                this.closeDialog();
            },
            error: (error: HttpErrorResponse) => {
              this.modalNotificationService.openErrorDialog({
                title: 'Creating Kubernetes secret failed', description: ErrorHelper.getErrorDetail(
                  error,
                  'Something went wrong with creating Kubernetes secret'
                  )
              });
            }
          });

      if (duplicate) {
        this.modalNotificationService.openConfirmDialog({
          headerTitle: 'Warning',
          title: 'Overwrite existing secret',
          description: `A Kubernetes secret "${secretName}" with key "${secretKey}" already exists in this scope. Do you want to overwrite it?`,
        }, {
          confirmButtonText: 'Overwrite'
        }).afterClosed().pipe(take(1)).subscribe((confirmed?: boolean) => {
          if (confirmed) {
            doCreate(true);
          }
        });
      } else {
        doCreate();
      }
    } else if (secretType == 'internal') {
      // TODO koenig 20260304: currently only Scope GLOBAL can be selected for secrets of type internal
      this.inventoryService.createTenantSecret(this.payload.tenantKey,
          secretValue, secretDescription).subscribe(() => {
          this.toastNotificationService.showSuccessToast(`Global ${this.inventoryResourceType.toLowerCase()} has been successfully created`, 'Successfully created');
          this.payload.onSaveCallback();
          this.closeDialog();
      }, (error: HttpErrorResponse) => {
          this.modalNotificationService.openErrorDialog({title: 'Creating global secret failed', description: ErrorHelper.getErrorDetail(error, 'Something went wrong with creating global secret')});
      });
    }
  }

  displayAttachmentResourceUploadToast(inventoryAttachmentRefs?: string): void {
    if (_.isEmpty(inventoryAttachmentRefs)) {
      return;
    }
    this.toastNotificationService.showSuccessToast(`Global ${this.inventoryResourceType.toLowerCase()} has been successfully uploaded`, 'Successfully uploaded');
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  protected readonly InventoryResourceType = InventoryResourceType;
}
